import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Drawer, Form, Input, Select, Spin } from "antd";
import PropTypes from "prop-types";
import { debounce } from "lodash";
import { DatePicker, useJalaliLocaleListener } from "antd-jalali";

import { Modals, Types } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";
import Rules from "scripts/rules";

const Component = ({ data = {}, onUpdate, options = [] }) => {
	const initialState = { users: options };
	const [state, setState] = useState(initialState);
	const { loading, submitting, visible, users } = state;
	const { id } = data;
	const { hash, pathname, search } = useLocation();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const handleSearch = debounce((search) => {
		if (search?.length > 1) {
			API.user
				.list({ paginate: false, search })
				.then(({ data }) => {
					setState((prevState) => ({
						...prevState,
						users: data.map((o) => ({
							value: o.id,
							label: `${o.firstName} ${o.lastName}`,
						})),
					}));
				})
				.catch(() => {});
		}
	}, 500);

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));

				values.general = true;

				if (!values.users) {
					delete values.users;
					values.general = true;
				}
				if (values.users?.length > 0) {
					values.general = false;
					values.users = values.users.map((value) => value);
				}

				if (values.expireAt) {
					values.expireAt = values.expireAt.format(
						process.env.REACT_APP_DATE_FORMAT
					);
				}

				const action =
					hash === `#${Modals.CREATE}`
						? API.notification.create(values)
						: API.notification.update(id, values);

				action
					.then(() => {
						onUpdate(null, true);
						navigate(-1);
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, submitting: false }));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		switch (hash) {
			case `#${Modals.CREATE}`: {
				setState((prevState) => ({ ...prevState, visible: true }));

				break;
			}
			case `#${Modals.EDIT}`: {
				if (id) {
					setState((prevState) => ({
						...prevState,
						visible: true,
						loading: true,
					}));

					API.notification
						.details(id)
						.then(({ data }) => {
							form.setFieldsValue({
								...data,
								users: data.users.map(({ id, fullName }) => ({
									value: id,
									label: fullName || "-",
								})),
							});

							setState((prevState) => ({
								...prevState,
								loading: false,
								users: data.users.map(({ id, fullName }) => ({
									value: id,
									label: fullName || "-",
								})),
							}));
						})
						.catch(() => {
							setState((prevState) => ({ ...prevState, loading: false }));
						});
				} else {
					navigate(`${pathname}${search}`, { replace: true });
				}

				break;
			}
			default: {
				if (visible) form.resetFields();

				setState(initialState);

				break;
			}
		}
	};

	useEffect(componentDidUpdate, [hash]);

	useJalaliLocaleListener();

	return (
		<Drawer
			className="sp-form-drawer"
			placement={process.env.REACT_APP_IS_RTL ? "left" : "right"}
			footer={
				<>
					<Button
						type="primary"
						onClick={handleSubmit}
						loading={submitting}
					>
						{Resources.confirm}
					</Button>
					<Button
						onClick={() => navigate(-1)}
						disabled={submitting}
					>
						{Resources.cancel}
					</Button>
				</>
			}
			onClose={() => navigate(-1)}
			title={`${
				hash === `#${Modals.CREATE}` ? Resources.add : Resources.edit
			} ${Resources.notification}`}
			maskClosable={false}
			open={visible}
			width={768}
		>
			<Form
				layout="vertical"
				form={form}
			>
				{loading ? (
					<Spin className="sp-center-spin" />
				) : (
					<>
						<Form.Item
							name="title"
							label={Resources.title}
							rules={Rules.required}
						>
							<Input />
						</Form.Item>
						<Form.Item
							name="body"
							label={Resources.text}
							rules={Rules.required}
						>
							<Input.TextArea rows={4} />
						</Form.Item>
						<Form.Item
							name="informType"
							label={`${Resources.type} ${Resources.notification}`}
							rules={Rules.required}
						>
							<Select
								options={[
									{ value: Types.notifications.info, label: Resources.info },
									{
										value: Types.notifications.success,
										label: Resources.successfully,
									},
									{
										value: Types.notifications.warning,
										label: Resources.warning,
									},
									{ value: Types.notifications.error, label: Resources.error },
								]}
							/>
						</Form.Item>
						<Form.Item
							name="expireAt"
							label={`${Resources.date} ${Resources.expire}`}
						>
							<DatePicker allowClear />
						</Form.Item>
						<Form.Item
							name="users"
							label={Resources.user}
						>
							<Select
								options={users.length > 0 ? users : options}
								onSearch={handleSearch}
								filterOption={false}
								showArrow={false}
								showSearch
								mode="multiple"
							/>
						</Form.Item>
					</>
				)}
			</Form>
		</Drawer>
	);
};

Component.propTypes = {
	data: PropTypes.object,
	onUpdate: PropTypes.func.isRequired,
};

export default Component;
